import type { FC, ReactNode, PropsWithChildren } from 'react';
import { Children, isValidElement, cloneElement } from 'react';
import { isElementWithProps } from '../../../utils/children';

/**
 * Wraps the "Mein Schiff" ligature with either
 * its trademark symbol or its ship number in a span.
 * The span is styled to be italic and not to break across lines.
 * This resolves some cross-browser issues with the "Mein Schiff" ligature.
 * Pure ligatures will for instance break on line-breaks.
 *
 * Simply wrap any given text in this component to get the desired effect.
 *
 * ```tsx
 * <MeinSchiffText>
 *   Discover Mein Schiff ®
 * </MeinSchiffText>
 * ```
 *
 * ```tsx
 * <MeinSchiffText>
 *   Discover Mein Schiff ®: <a href="https://mein-schiff.com">Discover now</a>
 * </MeinSchiffText>
 * ```
 *
 * ```tsx
 * <p>
 *   Lorem ipsum <MeinSchiffText>Mein Schiff ®</MeinSchiffText> dolor sit amet.
 * </p>
 * ```
 */
export const MeinSchiffText: FC<PropsWithChildren<{}>> = ({ children }) => {
  // Regex will match:
  // "Mein Schiff" plus an optional space and one of: "®" or digits 1-8 or "Flow" or "Relax"
  let pattern = /(Mein Schiff(?: (?:®|[1-8]|Flow|Relax))?)/g;

  /**
   * Splits a text string by the pattern and wraps the matches accordingly.
   */
  let processText = (text: string): ReactNode[] => {
    let nodes: ReactNode[] = [];
    let idxLast = 0;

    // Use matchAll to get all match info (text & index).
    for (let match of text.matchAll(pattern)) {
      let matchText = match[0];
      let idxStart = match.index ?? 0;

      // Push any text before this match as plain text.
      if (idxStart > idxLast) {
        nodes.push(text.slice(idxLast, idxStart));
      }

      // If the matched phrase contains '®', wrap it in an extra <span>.
      if (matchText.includes('®')) {
        // Example: "Mein Schiff ®" => ["Mein Schiff ", ""]
        let [prefix, suffix] = matchText.split('®');

        nodes.push(
          <span className="whitespace-nowrap" key={idxStart}>
            {prefix}
            <span className="">®</span>
            {suffix}
          </span>,
        );
      } else {
        nodes.push(
          <span className="whitespace-nowrap" key={idxStart}>
            {matchText}
          </span>,
        );
      }

      // Update the position after this match.
      idxLast = idxStart + matchText.length;
    }

    // Push the remaining text (if any) after the last match.
    if (idxLast < text.length) {
      nodes.push(text.slice(idxLast));
    }

    return nodes;
  };

  /**
   * Recursively processes each child. If it's a string, parse it with `processText`.
   * If it's a React element, clone it with its children processed.
   */
  let processChild = (child: ReactNode): ReactNode => {
    if (typeof child === 'string') {
      return processText(child);
    }

    // If it's something else (null, boolean, etc.), just return as is.
    if (!isValidElement(child)) {
      return child;
    }

    if (!isElementWithProps(child) || !child.props.children) {
      return child;
    }

    // ... dive in to the recursion rabbit hole
    return cloneElement(child, {
      ...child.props,
      children: Children.map(child.props.children, processChild),
    });
  };

  // Process all top-level children.
  return <>{Children.map(children, processChild)}</>;
};
